'use client';

import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import { useGetPendingEventsQuery } from 'services/event.service';
import { cn } from 'utils/helpers';
import EventCard from './EventCard';
import InfiniteList from './InfiniteList';
import EmptyEventsList from './EmptyEventsList';
import { Loader2 } from 'lucide-react';

import { IEvent } from 'types/event';
import { useAuth } from 'modules/auth/context/AuthContextV2';
import { compareEventDates } from './UpcomingEventsSection';

type Event = {
  event: IEvent;
};

const DraftsEventsSection = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [events, setEvents] = useState<Event[]>([]);
  const { data: draftEvents, isFetching } = useGetPendingEventsQuery({
    page,
    isPast: true,
  });

  const uniqueEvents = useMemo(() => {
    const filteredEventIds = new Set(events.map((event) => event.event._id));
    return draftEvents?.filter(
      (event: Event) => !filteredEventIds.has(event.event._id),
    );
  }, [draftEvents, events]);

  const filteredEvents = useMemo(() => {
    const uniqueEventIds = new Set();

    return events?.filter((event: Event) => {
      if (
        event.event.userId === user?.id &&
        !uniqueEventIds.has(event.event._id)
      ) {
        uniqueEventIds.add(event.event._id);
        return true;
      }
      return false;
    });
  }, [events, user]);

  const sortedEvents = useMemo(() => {
    return [...filteredEvents].sort(compareEventDates);
  }, [user, filteredEvents]);

  useEffect(() => {
    if (draftEvents) {
      if (draftEvents.length === 0) {
        setHasMore(false);
      }
      if (uniqueEvents.length === 0) {
        setHasMore(false);
      }
    }
  }, [draftEvents, uniqueEvents]);

  return (
    <div>
      <div className="w-full flex justify-between items-center px-5 pb-3">
        <p className="text-[1rem] font-bold">
          {sortedEvents?.length > 0
            ? `${sortedEvents?.length} events`
            : 'Events'}
        </p>
      </div>
      <div
        className={cn(
          'w-full max-h-[42svh] min-h-[21.75rem] flex flex-col gap-3 overflow-y-scroll',
          sortedEvents.length === 0 && 'max-h-[55svh]',
        )}
      >
        <InfiniteList
          onInView={() => {
            if (isFetching || !hasMore) return;

            setPage((prev) => prev + 1);
            setEvents((prev) => {
              const combinedEvents = [...prev, ...(draftEvents || [])];

              return combinedEvents.sort(compareEventDates);
            });
          }}
          children={
            <div
              className={cn(
                'w-full flex flex-col justify-center items-center gap-3 overflow-y-scroll px-5 mb-[9rem]',
              )}
            >
              {sortedEvents.length > 0 ? (
                sortedEvents.map((event: Event, index: number) => (
                  <div
                    className="w-full flex"
                    key={index}
                    onClick={() => {
                      navigate(`/lets-meet/edit/${event.event._id}`);
                    }}
                  >
                    <EventCard
                      title={event.event.activity}
                      description={event.event.details || ''}
                      startTime={event.event.start?.toString() || ''}
                      endTime={event.event.end?.toString() || ''}
                      emoji={event.event.emoji || '📅'}
                      organizerId={event.event.userId}
                      status={event.event.status}
                    />
                  </div>
                ))
              ) : (
                <>
                  {!isFetching && draftEvents.length === 0 && (
                    <EmptyEventsList />
                  )}
                  {isFetching && (
                    <div className="flex justify-center items-center">
                      <Loader2 className="w-4 h-4 animate-spin" />
                    </div>
                  )}
                </>
              )}
              {isFetching && (
                <p className="text-center text-gray-500">Loading...</p>
              )}
            </div>
          }
        />
      </div>
    </div>
  );
};

export default DraftsEventsSection;
